import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Link2 } from 'lucide-react';
import { bc } from '../lib/api';

function RouteLink({ s }) {
  const [acc, setAcc] = useState(s.razorpay_account_id || '');
  const save = () => bc.linkRoute(s.id, acc.trim()).then(() => toast.success('Linked')).catch(e => toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Failed'));
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border py-2 text-sm" data-testid={`route-row-${s.id}`}>
      <span className="flex-1 min-w-[160px]">{s.partner_company || s.name} <span className="text-ink/50">({(s.seller_type || '').replace('_', ' ')})</span></span>
      <input data-testid={`route-acc-${s.id}`} value={acc} onChange={e => setAcc(e.target.value)} placeholder="acc_XXXXXXXX" className="w-48 rounded-lg border border-border px-3 py-1.5"/>
      <button data-testid={`route-save-${s.id}`} onClick={save} className="btn-outline !py-1.5 text-xs"><Link2 className="w-3.5 h-3.5"/>Link</button>
    </div>
  );
}

export default function AdminCommission() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { bc.rates().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin"/>;
  const setRate = (c, v) => setD({ ...d, category_rates: { ...d.category_rates, [c]: v } });
  const save = async () => {
    setBusy(true);
    try {
      const num = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '').map(([k, v]) => [k, Number(v)]));
      const r = await bc.saveRates({ category_rates: num(d.category_rates), other_rate: Number(d.other_rate), seller_overrides: num(d.seller_overrides), brand_overrides: num(d.brand_overrides), promo_adjust_pct: Number(d.promo_adjust_pct) });
      setD({ ...d, ...r }); toast.success('Commission rates saved');
    } catch (e) { const det = e?.response?.data?.detail; toast.error((Array.isArray(det) ? det[0]?.msg : det) || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="admin-commission">
      <section className="card-soft p-4 space-y-3">
        <div className="font-heading font-bold">National commission rates (% of item value)</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {d.categories.map(c => (
            <label key={c} className="text-sm"><span className="text-xs text-ink/60">{c}</span>
              <input data-testid={`rate-${c}`} type="number" step="0.1" min="0" max="50" value={d.category_rates[c] ?? ''} onChange={e => setRate(c, e.target.value)} placeholder={`${d.other_rate} (default)`} className="mt-1 w-full rounded-lg border border-border px-3 py-1.5"/></label>
          ))}
          <label className="text-sm"><span className="text-xs text-ink/60">All other categories</span>
            <input data-testid="rate-other" type="number" step="0.1" value={d.other_rate} onChange={e => setD({ ...d, other_rate: e.target.value })} className="mt-1 w-full rounded-lg border border-border px-3 py-1.5"/></label>
          <label className="text-sm"><span className="text-xs text-ink/60">Promotional adjustment (% of rate, e.g. -20)</span>
            <input data-testid="rate-promo" type="number" step="1" value={d.promo_adjust_pct} onChange={e => setD({ ...d, promo_adjust_pct: e.target.value })} className="mt-1 w-full rounded-lg border border-border px-3 py-1.5"/></label>
        </div>
        <p className="text-xs text-ink/60">Seller-specific and brand agreements override category rates. GST of {d.fee_gst_pct}% is charged on the platform fee. Cancelled orders reverse the fee.</p>
        <button data-testid="rates-save" disabled={busy} onClick={save} className="btn-primary text-sm"><Save className="w-4 h-4"/>Save rates</button>
      </section>
      <section className="card-soft p-4">
        <div className="font-heading font-bold">Razorpay Route payout accounts</div>
        <p className="text-xs text-ink/60 mb-2">Create a Linked Account for each verified seller in your Razorpay dashboard (Route), then paste its account ID here. Online payments are then split automatically: the seller's share goes to them, held until delivery.</p>
        {d.sellers.length === 0 ? <p className="text-sm text-ink/60">No verified sellers yet.</p> : d.sellers.map(s => <RouteLink key={s.id} s={s}/>)}
      </section>
    </div>
  );
}
